import React from "react";
import { connect } from "react-redux";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Paper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import WarningIcon from "@material-ui/icons/Warning";
import { subscriptionWarningDismiss } from "../../actions";
import * as fromRoutes from "../../actions/types";
import { useRouter } from "utils";

const useStyles = makeStyles((theme) => ({
	dialogTitle: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		backgroundColor: "#fff3cd",
		color: "#856404",
	},
	icon: {
		color: "#856404",
	},
	content: {
		paddingTop: theme.spacing(2),
	},
	disableDate: {
		fontWeight: 700,
		color: theme.palette.error.main,
	},
	banner: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		padding: theme.spacing(1.5, 2),
		backgroundColor: "#fff3cd",
		borderLeft: "4px solid #ffc107",
		marginBottom: theme.spacing(2),
		borderRadius: theme.shape.borderRadius,
	},
	bannerText: {
		color: "#856404",
		fontWeight: 500,
		flexGrow: 1,
	},
	extendBtn: {
		marginLeft: "auto",
		flexShrink: 0,
		whiteSpace: "nowrap",
	},
}));

const formatDate = (dateStr) => {
	if (!dateStr) return "soon";
	const d = new Date(dateStr);
	return d.toLocaleDateString("en-NZ", { day: "numeric", month: "long", year: "numeric" });
};

/**
 * Modal popup shown once per login session when the account is in grace period.
 * Also exports a GracePeriodBanner for inline use on pages.
 */
const GracePeriodWarningModal = ({ open, daysRemaining, gracePeriodEnd, onDismiss, onGoToSubscription }) => {
	const classes = useStyles();
	return (
		<Dialog open={open} maxWidth="sm" fullWidth onClose={onDismiss}>
			<DialogTitle disableTypography className={classes.dialogTitle}>
				<WarningIcon className={classes.icon} />
				<Typography variant="h6" style={{ color: "#856404" }}>
					Payment Action Required
				</Typography>
			</DialogTitle>
			<DialogContent className={classes.content}>
				<Typography variant="body1" gutterBottom>
					A payment issue has been detected on your account. Your organisation will continue to have full access for{" "}
					<span className={classes.disableDate}>{daysRemaining} more day{daysRemaining !== 1 ? "s" : ""}</span>
					{" "}(until <span className={classes.disableDate}>{formatDate(gracePeriodEnd)}</span>).
				</Typography>
				<Typography variant="body1" style={{ marginTop: 12 }}>
					After this period, staff members will be unable to log in and only administrators will have access to the subscription page.
				</Typography>
				<Typography variant="body1" style={{ marginTop: 12 }}>
					Please update your payment details or resubscribe to avoid any disruption.
				</Typography>
			</DialogContent>
			<DialogActions>
				<Button onClick={onDismiss} color="default">
					Dismiss
				</Button>
				<Button onClick={onGoToSubscription} color="primary" variant="contained">
					Go to Subscription
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export const GracePeriodBanner = ({ daysRemaining, gracePeriodEnd, subscriptionStatus, onExtendClick }) => {
	const classes = useStyles();
	if (subscriptionStatus !== "GracePeriod" && subscriptionStatus !== "Disabled") return null;

	const isDisabled = subscriptionStatus === "Disabled";
	const bannerStyle = isDisabled
		? { backgroundColor: "#f8d7da", borderLeft: "4px solid #dc3545" }
		: {};
	const textStyle = isDisabled ? { color: "#721c24" } : {};

	return (
		<Paper className={classes.banner} elevation={0} style={bannerStyle}>
			<WarningIcon style={isDisabled ? { color: "#721c24" } : { color: "#856404" }} />
			<Typography variant="body2" className={classes.bannerText} style={textStyle}>
				{isDisabled
					? "Your account is inactive. Please update your payment details or resubscribe to restore access."
					: `Payment issue detected. Account access will be restricted on ${formatDate(gracePeriodEnd)} (${daysRemaining} day${daysRemaining !== 1 ? "s" : ""} remaining).`}
			</Typography>
			{onExtendClick && (
				<Button
					className={classes.extendBtn}
					variant="outlined"
					size="small"
					style={isDisabled ? { borderColor: "#721c24", color: "#721c24" } : { borderColor: "#856404", color: "#856404" }}
					onClick={onExtendClick}
				>
					Extend Access
				</Button>
			)}
		</Paper>
	);
};

const GracePeriodWarningConnected = ({ showWarningPopup, warningDaysRemaining, gracePeriodEndDate, onDismiss }) => {
	const { history } = useRouter();

	const handleGoToSubscription = () => {
		onDismiss();
		history.push(fromRoutes.ACCOUNT_SUBSCRIPTION_ROUTE);
	};

	return (
		<GracePeriodWarningModal
			open={showWarningPopup}
			daysRemaining={warningDaysRemaining}
			gracePeriodEnd={gracePeriodEndDate}
			onDismiss={onDismiss}
			onGoToSubscription={handleGoToSubscription}
		/>
	);
};

const mapStateToProps = ({ subscription }) => ({
	showWarningPopup: subscription.showWarningPopup,
	warningDaysRemaining: subscription.warningDaysRemaining,
	gracePeriodEndDate: subscription.gracePeriodEndDate,
});

const mapDispatchToProps = {
	onDismiss: subscriptionWarningDismiss,
};

export default connect(mapStateToProps, mapDispatchToProps)(GracePeriodWarningConnected);
